/*
  Component: Product card add-to-cart and colour switching
  Responsibility: decide what the + button on a product card does, which variant an
                  add-to-cart sends, which product in a group the card is showing, and
                  whether the card is showing its lifestyle image on the way to the product.

  A card is one face per product in its group. Every face is a complete card — its own
  images, title, price, <product-form> and variant swatches, all server-rendered for that
  product — and exactly one is visible. Two consequences run through this whole file:

    * switching sibling is unhiding a face. Nothing is rewritten, fetched or re-bound,
      and the + button on the new face is already wired to the right product.
    * everything to do with the + button is scoped to a FACE, never to the card. A card
      with four siblings has four toggles and four size rows, and reaching for the card
      would always find the first one rather than the one on screen.

  Two shapes of face, decided in snippets/kl-product-card-face.liquid, not here:

    single variant   the + button is itself the form's submit button. This file never runs
                     for it, and it adds to cart with JavaScript switched off.

    many variants    the + button is a toggle that reveals the variant row, and each
                     swatch is a submit button carrying its own variant id. This file is
                     what turns a swatch press into the right variant.

  The add itself is Dawn's. <product-form> and assets/product-form.js own the fetch, the
  cart drawer refresh and the error message; all this file does is write the variant id
  into the hidden input that product-form.js:34 reads through FormData, and then let the
  submit event travel. Nothing here calls the cart API.

  Why the hidden input and not the button's own value: product-form.js builds its body
  with `new FormData(this.form)`, and FormData deliberately omits the submitter's
  name/value pair. A swatch's value therefore never reaches the request when JavaScript is
  running. It is still spelled out in the markup because it is exactly what a native form
  post uses when JavaScript is not.

  Everything is delegated from the document rather than bound per card. The collection
  grid is replaced wholesale by assets/facets.js on every filter, sort and page change,
  and re-inserted markup carries no listeners — a per-card init would have to be re-run on
  each of those, and would silently stop working the day one of them changed.
*/

/* Prefixed with the file's own name: every authored script shares one global scope, so a bare
   const here would collide with the next file that wanted the same word. */
const KL_PRODUCT_CARD_PREVIEWING_CLASS = 'is-previewing';

/*
  Pure: whether a click that landed anywhere in the document should close an open row. A
  click inside the face that owns the row is the shopper still using it.
*/
function getIsDismissingClick(openFaceElement, clickedElement) {
  if (!openFaceElement || !clickedElement) return false;

  return !openFaceElement.contains(clickedElement);
}

function closeSizeRow(faceElement) {
  if (!faceElement) return;

  const toggleElement = faceElement.querySelector('[data-kl-product-card-size-toggle]');
  const sizesElement = faceElement.querySelector('[data-kl-product-card-sizes]');
  const addElement = faceElement.querySelector('[data-kl-product-card-add]');

  if (!toggleElement || !sizesElement) return;

  sizesElement.hidden = true;
  toggleElement.setAttribute('aria-expanded', 'false');
  if (addElement) addElement.hidden = false;
}

function openSizeRow(faceElement) {
  if (!faceElement) return;

  const toggleElement = faceElement.querySelector('[data-kl-product-card-size-toggle]');
  const sizesElement = faceElement.querySelector('[data-kl-product-card-sizes]');
  const addElement = faceElement.querySelector('[data-kl-product-card-add]');

  if (!toggleElement || !sizesElement) return;

  sizesElement.hidden = false;
  toggleElement.setAttribute('aria-expanded', 'true');

  /*
    The row takes the corner the button was sitting in, so the button steps aside. This is
    the `hidden` attribute and not a width or a position — CSS owns those.
  */
  if (addElement) addElement.hidden = true;

  const firstSizeElement = sizesElement.querySelector('[data-kl-product-card-size]:not([disabled])');
  if (firstSizeElement) firstSizeElement.focus();
}

function closeEveryOpenSizeRow(exceptFaceElement) {
  document.querySelectorAll('[data-kl-product-card-face]').forEach((faceElement) => {
    if (faceElement === exceptFaceElement) return;

    closeSizeRow(faceElement);
  });
}

/*
  Write the chosen variant into the hidden input the form posts. The submit event is
  deliberately not cancelled: letting it through is what hands the add to product-form.js.
*/
function selectSizeVariant(sizeElement) {
  const faceElement = sizeElement.closest('[data-kl-product-card-face]');
  if (!faceElement) return;

  const variantInputElement = faceElement.querySelector('[data-kl-product-card-variant-input]');
  const variantId = sizeElement.dataset.klProductCardVariantId;

  if (!variantInputElement || !variantId) {
    console.debug('kl-product-card: cannot select variant, input or id missing', {
      hasVariantInput: Boolean(variantInputElement),
      variantId,
    });
    return;
  }

  variantInputElement.value = variantId;

  const preOrderSkuInput = faceElement.querySelector('[data-kl-pre-order-sku]');
  const preOrderStatusInput = faceElement.querySelector('[data-kl-pre-order-status]');
  const isPreOrder = sizeElement.dataset.klProductCardVariantPreOrder === 'true';
  if (preOrderSkuInput) {
    preOrderSkuInput.disabled = !isPreOrder;
    preOrderSkuInput.value = sizeElement.dataset.klProductCardVariantSku || '';
  }
  if (preOrderStatusInput) preOrderStatusInput.disabled = !isPreOrder;
}

/*
  Swap which product the card is showing. Returns whether a face actually matched, so
  the caller can leave the swatch's own link alone when one did not.
*/
function selectColorFace(cardElement, productId) {
  const faceElements = cardElement.querySelectorAll('[data-kl-product-card-face]');
  if (!faceElements.length) return false;

  let hasMatched = false;

  faceElements.forEach((faceElement) => {
    const isMatch = faceElement.dataset.klProductCardFace === productId;

    if (isMatch) hasMatched = true;
  });

  if (!hasMatched) {
    console.debug('kl-product-card: no face for the chosen colour', { productId });
    return false;
  }

  faceElements.forEach((faceElement) => {
    const isMatch = faceElement.dataset.klProductCardFace === productId;

    /* Any row left open on the face being hidden would come back open behind it. */
    if (!isMatch) closeSizeRow(faceElement);
    faceElement.hidden = !isMatch;
  });

  updateLightColorSwatches(cardElement);

  cardElement.querySelectorAll('[data-kl-product-card-color]').forEach((colorElement) => {
    const isCurrent = colorElement.dataset.klProductCardColor === productId;

    colorElement.classList.toggle('kl-product-card__color-link--current', isCurrent);
    if (isCurrent) {
      colorElement.setAttribute('aria-current', 'true');
    } else {
      colorElement.removeAttribute('aria-current');
    }
  });

  return true;
}

function updateLightColorSwatches(scope = document) {
  scope.querySelectorAll('.kl-product-card__color-swatch').forEach((swatchElement) => {
    const color = getComputedStyle(swatchElement).backgroundColor;
    const channels = color.match(/\d+(?:\.\d+)?/g)?.map(Number);

    if (!channels || channels.length < 3) return;

    const [red, green, blue] = channels.map((channel) => channel / 255);
    const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
    const colorRange = Math.max(red, green, blue) - Math.min(red, green, blue);

    swatchElement.classList.toggle(
      'kl-product-card__color-swatch--light',
      luminance >= 0.85 && colorRange <= 0.18,
    );
  });
}

function handleDocumentClick(event) {
  if (!event.target || typeof event.target.closest !== 'function') return;

  const colorElement = event.target.closest('[data-kl-product-card-color]');

  if (colorElement) {
    const cardElement = colorElement.closest('[data-kl-product-card]');

    /*
      The swatch is an anchor to that product, which is the only honest thing it can be
      with JavaScript off. The navigation is cancelled only once the swap has actually
      happened — if no face matched, the link is left to do its job.
    */
    if (cardElement && selectColorFace(cardElement, colorElement.dataset.klProductCardColor)) {
      event.preventDefault();
    }
    return;
  }

  const toggleElement = event.target.closest('[data-kl-product-card-size-toggle]');

  if (toggleElement) {
    const faceElement = toggleElement.closest('[data-kl-product-card-face]');
    const isOpen = toggleElement.getAttribute('aria-expanded') === 'true';

    closeEveryOpenSizeRow(faceElement);

    if (isOpen) {
      closeSizeRow(faceElement);
    } else {
      openSizeRow(faceElement);
    }
    return;
  }

  const sizeElement = event.target.closest('[data-kl-product-card-size]');

  if (sizeElement) {
    try {
      selectSizeVariant(sizeElement);

      /*
        Put the + button back before the submit travels, so the add has something visible to
        happen on.

        Dawn's product-form.js drives the busy state off `this.querySelector('[type=submit]')`
        and the first `.loading__spinner` in the form — the button it finds, not the button
        that was pressed. On a card with sizes that is the + button, and openSizeRow had
        hidden it to make room for the row. So the spinner was being shown on a hidden
        element and the card sat still for the second or two before the drawer opened, while
        a card with no sizes span its + and looked alive.

        Closing the row here restores the button Dawn is about to decorate. The submit is not
        cancelled and the click's default action is already on its way, so the add still goes
        — and the variant it posts comes from the hidden input selectSizeVariant just wrote,
        not from the submitter, which is why hiding the pressed button costs nothing.
      */
      const faceElement = sizeElement.closest('[data-kl-product-card-face]');

      closeSizeRow(faceElement);

      /*
        Follow the button that was just hidden. Without this the pressed size goes into a
        hidden container and focus falls to the body, which strands anyone who chose the size
        with a keyboard part-way through their own add. The + is where the state they are
        waiting on is about to appear, so it is the honest place to leave them.

        Not inside closeSizeRow: that also runs when a click elsewhere dismisses the row, and
        moving focus then would take it from whatever the visitor actually clicked.
      */
      const addElement = faceElement?.querySelector('[data-kl-product-card-add]');

      if (addElement) addElement.focus({ preventScroll: true });
    } catch (error) {
      /*
        A face that cannot work out its variant must not also submit the wrong one, so the
        add is stopped rather than allowed to fall through to the default variant.
      */
      event.preventDefault();
      console.debug('kl-product-card: variant selection failed', { error });
    }
    return;
  }

  const mediaElement = event.target.closest('[data-kl-product-card-media]');

  /*
    Show the lifestyle image while the product page loads, the way :hover does on a mouse.

    It has to happen HERE, in the click, and nowhere earlier. iOS abandons a tap whose
    content changed underneath it and waits for a second one — that is what the old :hover
    rule cost us, and setting this class on :active or on pointerdown reproduced it exactly.
    By the time a click handler runs the tap has already been accepted, so the swap is free.

    Nothing removes it. The page is on its way out and this card is what the visitor looks at
    until the product page paints. A card restored from the back-forward cache is cleared
    further down, or it would come back swapped for good.
  */
  if (mediaElement) {
    mediaElement.classList.add(KL_PRODUCT_CARD_PREVIEWING_CLASS);
    return;
  }

  document.querySelectorAll('[data-kl-product-card-sizes]:not([hidden])').forEach((sizesElement) => {
    const faceElement = sizesElement.closest('[data-kl-product-card-face]');

    if (getIsDismissingClick(faceElement, event.target)) closeSizeRow(faceElement);
  });
}

/*
  Going back does not reload the page — Safari and Chrome both restore it from the
  back-forward cache exactly as it was left, which means the card the visitor tapped would
  still be showing its lifestyle image, permanently and for no reason. Clear it on the way in.
*/
function handlePageShow() {
  document
    .querySelectorAll(`[data-kl-product-card-media].${KL_PRODUCT_CARD_PREVIEWING_CLASS}`)
    .forEach((mediaElement) => mediaElement.classList.remove(KL_PRODUCT_CARD_PREVIEWING_CLASS));
}

updateLightColorSwatches();

function handleDocumentKeydown(event) {
  if (event.key !== 'Escape') return;
  if (!event.target || typeof event.target.closest !== 'function') return;

  const faceElement = event.target.closest('[data-kl-product-card-face]');
  if (!faceElement) return;

  const toggleElement = faceElement.querySelector('[data-kl-product-card-size-toggle]');

  closeSizeRow(faceElement);
  if (toggleElement) toggleElement.focus();
}

/*
  Once the add is on its way the row has done its job. Listening for submit rather than
  for a cart event keeps this file off Dawn's pub/sub bus, which is a global.
*/
function handleDocumentSubmit(event) {
  if (!event.target || typeof event.target.closest !== 'function') return;

  const faceElement = event.target.closest('[data-kl-product-card-face]');
  if (!faceElement) return;

  /*
    Focus is on the swatch that is about to be hidden, so it moves back to the toggle
    first. product-form.js has already recorded the active element by this point — its own
    listener is on the form and runs before this one, which is on the document — so the
    cart drawer still restores focus where it means to.
  */
  const toggleElement = faceElement.querySelector('[data-kl-product-card-size-toggle]');
  const hasFocusInside = faceElement.contains(document.activeElement);

  closeSizeRow(faceElement);
  if (toggleElement && hasFocusInside) toggleElement.focus();
}

document.addEventListener('click', handleDocumentClick);
document.addEventListener('keydown', handleDocumentKeydown);
document.addEventListener('submit', handleDocumentSubmit);
window.addEventListener('pageshow', handlePageShow);
